import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { RoleKey } from '../models/role.model';

interface Envelope<T> {
  metadata: { message: string };
  data: T;
}

export type FirmApprovalStatus = 'pending' | 'approved' | 'rejected';

export interface FirmMe {
  firm: { _id: string; name: string; type: 'solo' | 'firm'; approval_status: FirmApprovalStatus };
  role: RoleKey;
  member_id: string;
}

export interface ApiMember {
  _id: string;
  firm_id: string;
  user_id: string | null;
  role: RoleKey;
  status: 'invited' | 'active' | 'suspended';
  email: string;
  /** Account name. Null while the member is still invited — they have no user record yet. */
  name: string | null;
  /** How many clients this member is assigned to. */
  assigned_clients: number;
  joined_at: string | null;
}

export interface ApiClientAccess {
  id: string;
  client_user_id: string;
  client_name: string;
  client_email: string;
  status: 'pending' | 'active' | 'revoked';
  /** Derived working state for the badge (from reviews + sign-off). */
  work_status: 'awaiting' | 'review' | 'query' | 'signed_off';
  scope: string[];
  tax_years: string[];
  invited_by: 'firm' | 'client';
  granted_at: string | null;
  signed_off_years: string[];
  assigned_member_id: string | null;
}

/**
 * Typed HTTP client for the accountant firm API (backend: /firm/*). Auth rides the shared cookie
 * session + CSRF header (handled by the auth interceptor). The workspace stores may fall back to
 * sample data when the backend isn't reachable — this service just does the calls.
 */
@Injectable({ providedIn: 'root' })
export class FirmApiService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/firm`;
  private readonly opts = { withCredentials: true };

  private unwrap<T>(o: Observable<Envelope<T>>): Observable<T> {
    return o.pipe(map((r) => r.data));
  }

  // --- firm + team ---
  createFirm(name: string, type: 'solo' | 'firm'): Observable<FirmMe['firm']> {
    return this.unwrap(this.http.post<Envelope<FirmMe['firm']>>(this.base, { name, type }, this.opts));
  }
  me(): Observable<FirmMe> {
    return this.unwrap(this.http.get<Envelope<FirmMe>>(`${this.base}/me`, this.opts));
  }
  team(): Observable<ApiMember[]> {
    return this.unwrap(this.http.get<Envelope<ApiMember[]>>(`${this.base}/team`, this.opts));
  }
  invite(email: string, role: RoleKey): Observable<{ member: ApiMember; token: string }> {
    return this.unwrap(this.http.post<Envelope<{ member: ApiMember; token: string }>>(`${this.base}/team/invite`, { email, role }, this.opts));
  }
  changeRole(memberId: string, role: RoleKey): Observable<ApiMember> {
    return this.unwrap(this.http.patch<Envelope<ApiMember>>(`${this.base}/team/${memberId}/role`, { role }, this.opts));
  }
  removeMember(memberId: string): Observable<unknown> {
    return this.http.delete(`${this.base}/team/${memberId}`, this.opts);
  }
  acceptInvite(token: string): Observable<ApiMember> {
    return this.unwrap(this.http.post<Envelope<ApiMember>>(`${this.base}/invites/accept`, { token }, this.opts));
  }

  /** Public: resolve an invite token to its firm + role + email (no auth needed). */
  invitePreview(token: string): Observable<{ firm_name: string; role: string; email: string }> {
    return this.unwrap(
      this.http.get<Envelope<{ firm_name: string; role: string; email: string }>>(
        `${this.base}/invites/${encodeURIComponent(token)}`,
      ),
    );
  }

  // --- access requests + clients + assignment ---
  accessRequests(): Observable<ApiClientAccess[]> {
    return this.unwrap(this.http.get<Envelope<ApiClientAccess[]>>(`${this.base}/access-requests`, this.opts));
  }
  respondRequest(accessId: string, accept: boolean): Observable<unknown> {
    return this.http.post(`${this.base}/access-requests/${accessId}/respond`, { accept }, this.opts);
  }
  clients(): Observable<ApiClientAccess[]> {
    return this.unwrap(this.http.get<Envelope<ApiClientAccess[]>>(`${this.base}/clients`, this.opts));
  }
  /** Firm-initiated invite: ask a client (by their HashBooks email) to share their books. */
  connectClient(clientEmail: string, scope: string[], taxYears?: string[]): Observable<unknown> {
    return this.http.post(`${this.base}/clients/connect`, { clientEmail, scope, taxYears }, this.opts);
  }
  assign(accessId: string, memberId: string): Observable<unknown> {
    return this.http.post(`${this.base}/clients/${accessId}/assign`, { memberId }, this.opts);
  }

  // --- guarded client data ---
  /** Headline figures for one tax year — pass the same FY start year as the transaction list. */
  clientOverview(accessId: string, financialYear?: number | string): Observable<any> {
    const qs = financialYear ? `?financial_year=${encodeURIComponent(String(financialYear))}` : '';
    return this.unwrap(this.http.get<Envelope<any>>(`${this.base}/clients/${accessId}/overview${qs}`, this.opts));
  }
  /**
   * The client's transactions, narrowed to one tax year. `financialYear` is the FY *start* year —
   * the backend resolves the country-specific window (UK 6 Apr, IN 1 Apr, US calendar).
   */
  clientTransactions(accessId: string, financialYear?: number | string): Observable<any> {
    const qs = financialYear ? `?financial_year=${encodeURIComponent(String(financialYear))}` : '';
    return this.unwrap(this.http.get<Envelope<any>>(`${this.base}/clients/${accessId}/transactions${qs}`, this.opts));
  }
  clientTax(accessId: string, financialYear: string): Observable<any> {
    return this.unwrap(this.http.get<Envelope<any>>(`${this.base}/clients/${accessId}/tax?financial_year=${encodeURIComponent(financialYear)}`, this.opts));
  }
  /** Lock a tax year as reviewed and final. 400s while flags are outstanding. */
  signOff(accessId: string, financialYear: number | string): Observable<any> {
    return this.unwrap(
      this.http.post<Envelope<any>>(
        `${this.base}/clients/${accessId}/sign-off`,
        { financial_year: String(financialYear) },
        this.opts,
      ),
    );
  }

  undoSignOff(accessId: string, financialYear: number | string): Observable<any> {
    return this.unwrap(
      this.http.delete<Envelope<any>>(
        `${this.base}/clients/${accessId}/sign-off?financial_year=${encodeURIComponent(String(financialYear))}`,
        this.opts,
      ),
    );
  }

  /** Per-disposal CGT report for one tax year. Returns the raw CSV as a Blob for download. */
  downloadReport(accessId: string, financialYear: number | string): Observable<Blob> {
    return this.http.get(
      `${this.base}/clients/${accessId}/report?financial_year=${encodeURIComponent(String(financialYear))}`,
      { ...this.opts, responseType: 'blob' },
    );
  }

  clientPortfolio(accessId: string): Observable<any> {
    return this.unwrap(this.http.get<Envelope<any>>(`${this.base}/clients/${accessId}/portfolio`, this.opts));
  }

  // --- work: queries, notes, reviews ---
  queries(accessId: string): Observable<any[]> {
    return this.unwrap(this.http.get<Envelope<any[]>>(`${this.base}/clients/${accessId}/queries`, this.opts));
  }
  createQuery(accessId: string, text: string, txnRef?: string): Observable<any> {
    return this.unwrap(this.http.post<Envelope<any>>(`${this.base}/clients/${accessId}/queries`, { text, txnRef }, this.opts));
  }
  notes(accessId: string): Observable<any[]> {
    return this.unwrap(this.http.get<Envelope<any[]>>(`${this.base}/clients/${accessId}/notes`, this.opts));
  }
  createNote(accessId: string, text: string, txnRef?: string): Observable<any> {
    return this.unwrap(this.http.post<Envelope<any>>(`${this.base}/clients/${accessId}/notes`, { text, txnRef }, this.opts));
  }
  setReview(accessId: string, txnRef: string, state: { reviewed?: boolean; flagged?: boolean }): Observable<any> {
    return this.unwrap(this.http.post<Envelope<any>>(`${this.base}/clients/${accessId}/review`, { txnRef, ...state }, this.opts));
  }
}
